import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, MessageCircle, Megaphone, BarChart3 } from "lucide-react";
import { usePlatformSettings } from "@/contexts/PlatformSettingsContext";

// Prefetch a lazy route's JS chunk on hover/touchstart so tapping the
// tab feels instant — the module lands in the browser cache before the
// finger lifts. Idempotent: calling it multiple times is free.
const prefetchers = {
  "/professional/bookings": () => import("@/pages/artisan/Bookings"),
  "/professional/chats": () => import("@/pages/artisan/Chats"),
  "/professional/promote": () => import("@/pages/artisan/PromotePicker"),
  "/professional/analytics": () => import("@/pages/artisan/Analytics"),
};
const triggeredPrefetch = new Set();
const prefetchRoute = (path) => {
  const cleanPath = (path || "").split("?")[0];
  if (triggeredPrefetch.has(cleanPath)) return;
  const p = prefetchers[cleanPath];
  if (p) {
    triggeredPrefetch.add(cleanPath);
    try { p(); } catch {}
  }
};

const HOME_LOGO_URL = "https://customer-assets.emergentagent.com/job_ghana-marketplace-5/artifacts/wluohaai_FBABBFB0-B86A-40AF-A319-0D7428AF7412.png";

export default function ArtisanFooter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const platformSettings = usePlatformSettings();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/messages/unread-count?token=${token}`);
        setUnreadCount(res.data?.count || 0);
      } catch (e) {
        // Ignore errors
      }
    };
    fetchUnreadCount();
    // Tight polling so the red bubble on the Messages tab feels instant
    // (Apple-style badge). 10 s picks up incoming messages promptly while
    // staying well within the shared polling budget the app targets.
    const interval = setInterval(fetchUnreadCount, 10000);
    const onVis = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener("visibilitychange", onVis);
    const onMsgSync = () => fetchUnreadCount();
    window.addEventListener("yorm:messages-updated", onMsgSync);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("yorm:messages-updated", onMsgSync);
    };
  }, [token]);

  // Fetch pending bookings count for artisan
  useEffect(() => {
    const fetchPendingBookings = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/bookings?token=${token}`);
        const bookings = res.data || [];
        // Badge stays on for every non-terminal booking — pending,
        // searching, negotiating, accepted, en_route, in_progress, etc.
        const TERMINAL_STATUSES = new Set([
          "completed", "cancelled", "canceled", "declined", "expired", "rejected",
        ]);
        setPendingBookingsCount(bookings.filter((b) => !TERMINAL_STATUSES.has(b.status)).length);
      } catch (e) {
        // Ignore errors
      }
    };
    fetchPendingBookings();
    const interval = setInterval(fetchPendingBookings, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const isActive = (path, exact = false) => {
    // Strip the query string from `path` before comparing so tabs like
    // `/professional/bookings?status=active` still light up when the user
    // is on `/professional/bookings`. Without this the Bookings icon never
    // gets the thicker stroke + magnify treatment applied to every other tab.
    const cleanPath = (path || "").split("?")[0];
    if (exact) return location.pathname === cleanPath;
    return location.pathname === cleanPath || location.pathname.startsWith(cleanPath + "/");
  };

  // Handle home button click - scroll to top or navigate to home
  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/professional" || location.pathname === "/professional/") {
      // Already on home page, just scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Navigate to home and scroll to top
      navigate("/professional");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const navItems = [
    { path: "/professional/bookings?status=active", icon: Calendar, label: "Bookings", badge: pendingBookingsCount },
    ...(platformSettings.enable_chat ? [{ path: "/professional/chats", icon: MessageCircle, label: "Messages", badge: unreadCount }] : []),
    { path: "/professional", icon: null, label: "Home", isHome: true, exact: true },
    ...(platformSettings.enable_promotions ? [{ path: "/professional/promote", icon: Megaphone, label: "Promote" }] : []),
    { path: "/professional/analytics", icon: BarChart3, label: "Analytics" },
  ];

  return (
    // GPU-promote via `translate-z-0` + `will-change:transform` so iOS
    // Safari keeps the footer anchored to the true viewport bottom instead
    // of momentarily floating up when the URL bar collapses/expands. The
    // border/shadow now live on the outer <footer> AND the safe-area
    // inset lives INSIDE the bordered region so tab row + home-indicator
    // strip read as one seamless block.
    <footer
      className="fixed bottom-0 left-0 right-0 z-[1100] md:hidden apple-bar apple-bar-bottom [transform:translateZ(0)] will-change-transform"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <nav className="flex items-center justify-around h-16 px-2 bg-transparent">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            
            // Home button has special onClick handler
            if (item.isHome) {
              return (
                <button
                  key={item.path}
                  onClick={handleHomeClick}
                  className={`group flex flex-col items-center justify-center flex-1 h-full py-1.5 transition-colors cursor-pointer ${
                    active ? "text-[#A67E2A]" : "text-gray-500 hover:text-[#D4A853]"
                  }`}
                  data-testid={`footer-${item.label.toLowerCase()}`}
                >
                  <div
                    className={`flex flex-col items-center justify-center px-3 py-1 transition-transform duration-200 group-active:scale-125 ${
                      active ? "scale-125" : ""
                    }`}
                  >
                    <div className="relative flex items-center justify-center h-7">
                      <img
                        src={HOME_LOGO_URL}
                        alt="Home"
                        className="w-7 h-7 object-contain"
                      />
                    </div>
                    <span className={`text-[10px] mt-0.5 tracking-tight ${active ? "font-bold" : "font-medium"}`}>
                      {item.label}
                    </span>
                  </div>
                </button>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onMouseEnter={() => prefetchRoute(item.path)}
                onTouchStart={() => prefetchRoute(item.path)}
                className={`group flex flex-col items-center justify-center flex-1 h-full py-1.5 transition-colors cursor-pointer ${
                  active ? "text-[#A67E2A]" : "text-gray-500 hover:text-[#D4A853]"
                }`}
                data-testid={`footer-${item.label.toLowerCase()}`}
              >
                <div
                  className={`flex flex-col items-center justify-center px-3 py-1 transition-transform duration-200 group-active:scale-110 ${
                    active ? "scale-110" : ""
                  }`}
                >
                  <div className="relative flex items-center justify-center h-7">
                    <Icon size={24} strokeWidth={active ? 2.75 : 2} />
                    {item.badge > 0 && (
                      <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-gradient-to-br from-[#FF453A] to-[#D70015] text-white text-[10px] font-bold tabular-nums rounded-full flex items-center justify-center ring-2 ring-white shadow-[0_2px_6px_rgba(215,0,21,0.45)]" data-testid={`footer-${item.label.toLowerCase()}-badge`}>
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] mt-0.5 tracking-tight ${active ? "font-bold" : "font-medium"}`}>
                    {item.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>
    </footer>
  );
}
